import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import { ModuleRegistry } from '@ag-grid-community/core'
import type {
  CellClassParams,
  CellClickedEvent,
  ColDef,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridReadyEvent,
  ICellRendererParams,
  IServerSideDatasource,
  PaginationChangedEvent,
  SelectionChangedEvent,
  SortChangedEvent,
} from '@ag-grid-community/core'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { Dropdown } from '@surecompanies/core_components'
import MoreOutlined from '@ant-design/icons/MoreOutlined'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import type { SelectionMode } from './ThreeStateHeaderCheckbox'
import SelectionBanner from './SelectionBanner'
import type { PlanBenefitOverrideRuleResponseDto } from '../../../api/dto/plan-benefit-override-rules.dto'
import { applySortModel, DEFAULT_SORT } from '../../../utils/gridSortModel'
import {
  PlanBenefitOverrideRuleStatus,
  PlanBenefitOverrideRuleTemplateKey,
} from '../enums/plan-benefit-override-rule.enum'
import { useGetPlanBenefits } from '../hooks/useGetPlanBenefits'

ModuleRegistry.registerModules([ServerSideRowModelModule])

const DEFAULT_PAGE_SIZE = 20
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

// Fields that are applicable for PARTIAL_FIELD_UPDATE rows.
// All other param fields are UPSERT_RULE-only and will be greyed out.
const PARTIAL_UPDATE_FIELDS = new Set([
  'appliesToDeductible',
  'coinsurance',
  'copayAmount',
  'benefitDisplayValue',
  'notes',
])

// Returns cellClassRules that marks a cell as not-applicable when the row's
// template is PARTIAL_FIELD_UPDATE and the field is UPSERT_RULE-only.
const notApplicableClassRules = (field: string) => ({
  cellNotApplicable: (p: CellClassParams<PlanBenefitOverrideRuleResponseDto>) =>
    p.data?.template ===
      PlanBenefitOverrideRuleTemplateKey.PARTIAL_FIELD_UPDATE &&
    !PARTIAL_UPDATE_FIELDS.has(field),
})

const boolFormatter = (p: { value: unknown }) =>
  p.value === true ? '✓' : p.value === false ? '✗' : '—'

const numFormatter = (p: { value: unknown }) =>
  p.value !== null && p.value !== undefined ? String(p.value) : '—'

const currencyFormatter = (p: { value: unknown }) => {
  if (p.value === null || p.value === undefined) return '—'
  const n = Number(p.value)
  if (n === -1) return '-1'
  return '$' + n.toLocaleString('en-US')
}

const strFormatter = (p: { value: unknown }) =>
  p.value !== null && p.value !== undefined && p.value !== ''
    ? String(p.value)
    : '—'

interface Props {
  datasource: IServerSideDatasource
  defaultPageSize?: number
  pageSizeOptions?: number[]
  initialPage?: number
  initialSortModel?: { colId: string; sort: 'asc' | 'desc' }
  selectionMode: SelectionMode
  selectedCount: number
  totalCount: number
  isMultiPage: boolean
  programmaticSelectRef: React.MutableRefObject<boolean>
  onRowView: (ruleId: string) => void
  onRowApprove?: (ruleId: string) => void
  onRowReject?: (ruleId: string) => void
  onRowRedraft?: (ruleId: string) => void
  onRowDelete?: (ruleId: string) => void
  onSelectionChanged?: (ids: string[]) => void
  onHeaderCheckboxClicked: () => void
  onSelectAll: () => void
  onClearSelection: () => void
  onPageChanged?: () => void
  onPageChange?: (page: number) => void
  onPageSizeChange?: (size: number) => void
  onSortChange?: (colId: string, sort: 'asc' | 'desc') => void
}

const DEFAULT_COL_DEF: ColDef = {
  sortable: false,
  filter: false,
  resizable: true,
  wrapHeaderText: true,
}

const ROW_STYLE = { cursor: 'pointer' } as const

const getRowId = ({ data }: { data: PlanBenefitOverrideRuleResponseDto }) =>
  data.id

interface ActionsCellRendererParams
  extends ICellRendererParams<PlanBenefitOverrideRuleResponseDto> {
  onRowView: (ruleId: string) => void
  onRowApprove?: (ruleId: string) => void
  onRowReject?: (ruleId: string) => void
  onRowRedraft?: (ruleId: string) => void
  onRowDelete?: (ruleId: string) => void
}

const ActionsCellRenderer = ({
  data,
  onRowView,
  onRowApprove,
  onRowReject,
  onRowRedraft,
  onRowDelete,
}: ActionsCellRendererParams) => {
  if (!data) return null

  const status = data.status as PlanBenefitOverrideRuleStatus
  const statusItems = [
    ...(status !== PlanBenefitOverrideRuleStatus.APPROVED
      ? [{ key: 'approve', label: 'Approve', disabled: !onRowApprove }]
      : []),
    ...(status !== PlanBenefitOverrideRuleStatus.REJECTED
      ? [
          {
            key: 'reject',
            label: 'Reject',
            danger: true as const,
            disabled: !onRowReject,
          },
        ]
      : []),
    ...(status !== PlanBenefitOverrideRuleStatus.DRAFT
      ? [{ key: 'redraft', label: 'Re-draft', disabled: !onRowRedraft }]
      : []),
  ]

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items: [
          { key: 'view', label: 'View' },
          ...statusItems,
          {
            key: 'delete',
            label: 'Delete',
            danger: true as const,
            disabled: !onRowDelete,
          },
        ],
        onClick: ({ key, domEvent }) => {
          domEvent.stopPropagation()
          if (key === 'view') onRowView(data.id)
          else if (key === 'approve') onRowApprove?.(data.id)
          else if (key === 'reject') onRowReject?.(data.id)
          else if (key === 'redraft') onRowRedraft?.(data.id)
          else if (key === 'delete') onRowDelete?.(data.id)
        },
      }}
    >
      <a onClick={(e) => e.preventDefault()} aria-label="row actions">
        <MoreOutlined />
      </a>
    </Dropdown>
  )
}

interface SelectionHeaderCheckboxParams {
  selectionMode: SelectionMode
  onHeaderCheckboxClicked: () => void
}

const SelectionHeaderCheckbox = ({
  selectionMode,
  onHeaderCheckboxClicked,
}: SelectionHeaderCheckboxParams) => {
  const checkboxRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const el = checkboxRef.current
    if (!el) return
    el.checked = selectionMode === 'page'
    el.indeterminate = selectionMode === 'all'
  }, [selectionMode])

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
      }}
    >
      <div
        className={[
          'ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper',
          selectionMode === 'page' ? 'ag-checked' : '',
          selectionMode === 'all' ? 'ag-indeterminate' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role="presentation"
      >
        <input
          ref={checkboxRef}
          type="checkbox"
          className="ag-input-field-input ag-checkbox-input"
          aria-label="Select rows"
          tabIndex={-1}
          onChange={onHeaderCheckboxClicked}
        />
      </div>
    </div>
  )
}

const OverrideRulesGrid = forwardRef<
  AgGridReact<PlanBenefitOverrideRuleResponseDto>,
  Props
>(function OverrideRulesGrid(
  {
    datasource,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    initialPage = 1,
    initialSortModel,
    selectionMode,
    selectedCount,
    totalCount,
    isMultiPage,
    programmaticSelectRef,
    onRowView,
    onRowApprove,
    onRowReject,
    onRowRedraft,
    onRowDelete,
    onSelectionChanged,
    onHeaderCheckboxClicked,
    onSelectAll,
    onClearSelection,
    onPageChanged,
    onPageChange,
    onPageSizeChange,
    onSortChange,
  },
  ref
) {
  const { benefitsByName } = useGetPlanBenefits()
  const [grid, setGrid] =
    useState<GridReadyEvent<PlanBenefitOverrideRuleResponseDto>>()
  const gridInitializedRef = useRef(false)
  const programmaticSortRef = useRef(false)

  // Sync datasource into the grid whenever it changes (search / tab switch via key reset)
  useEffect(() => {
    if (!grid) return
    grid.api.setGridOption('serverSideDatasource', datasource)
    grid.api.refreshServerSide({ purge: true })
  }, [grid, datasource])

  // Sync grid to URL page when browser back/forward changes the page param after initial render.
  useEffect(() => {
    if (!grid || !gridInitializedRef.current) return
    grid.api.paginationGoToPage(initialPage - 1)
  }, [grid, initialPage])

  // Sync grid to URL sort when browser back/forward changes the sort param after initial render.
  // programmaticSortRef prevents handleSortChanged from calling onSortChange (which would reset
  // page to 1) when we are the ones initiating the sort change.
  useEffect(() => {
    if (!grid || !gridInitializedRef.current) return
    programmaticSortRef.current = true
    if (initialSortModel) {
      grid.api.applyColumnState({
        state: [{ colId: initialSortModel.colId, sort: initialSortModel.sort }],
        defaultState: { sort: null },
      })
    } else {
      grid.api.applyColumnState({ defaultState: { sort: null } })
    }
  }, [grid, initialSortModel])

  // Sync grid to URL pageSize when browser back/forward changes the pageSize param after initial render.
  useEffect(() => {
    if (!grid || !gridInitializedRef.current) return
    grid.api.setGridOption('paginationPageSize', defaultPageSize)
    grid.api.setGridOption('cacheBlockSize', defaultPageSize)
    grid.api.refreshServerSide({ purge: true })
  }, [grid, defaultPageSize])

  const handleGridReady = useCallback(
    (event: GridReadyEvent<PlanBenefitOverrideRuleResponseDto>) => {
      setGrid(event)
      event.api.autoSizeAllColumns(false)
    },
    []
  )

  const handleFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent<PlanBenefitOverrideRuleResponseDto>) => {
      if (selectionMode === 'all') {
        programmaticSelectRef.current = true
        event.api.selectAll()
      }
      event.api.autoSizeAllColumns(false)
      if (initialPage > 1) {
        event.api.paginationGoToPage(initialPage - 1)
      }
      gridInitializedRef.current = true
    },
    [selectionMode, programmaticSelectRef, initialPage]
  )

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<PlanBenefitOverrideRuleResponseDto>) => {
      if (!onSelectionChanged) return
      if (programmaticSelectRef.current) {
        programmaticSelectRef.current = false
        return
      }
      // getSelectedRows() returns [] synchronously in SSRM; iterate nodes instead.
      const ids: string[] = []
      event.api.forEachNode((node) => {
        if (node.isSelected() && node.data?.id) ids.push(node.data.id)
      })
      onSelectionChanged(ids)
    },
    [onSelectionChanged, programmaticSelectRef]
  )

  const handleCellClicked = useCallback(
    (event: CellClickedEvent<PlanBenefitOverrideRuleResponseDto>) => {
      const colId = event.column.getColId()
      if (colId === 'actions' || colId === 'selection') return
      const ruleId = event.data?.id
      if (!ruleId) return
      onRowView(ruleId)
    },
    [onRowView]
  )

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<PlanBenefitOverrideRuleResponseDto>) => {
      return [
        {
          name: 'Copy Cell',
          action: () => {
            const value = params.value
            if (value != null) {
              void navigator.clipboard.writeText(String(value))
            }
          },
        },
      ]
    },
    []
  )

  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent<PlanBenefitOverrideRuleResponseDto>) => {
      if (event.newPage) {
        onPageChanged?.()
        if (gridInitializedRef.current) {
          onPageChange?.(event.api.paginationGetCurrentPage() + 1)
        }
      }
      if (event.newPageSize && gridInitializedRef.current) {
        onPageSizeChange?.(event.api.paginationGetPageSize())
      }
    },
    [onPageChanged, onPageChange, onPageSizeChange]
  )

  const handleSortChanged = useCallback(
    (event: SortChangedEvent<PlanBenefitOverrideRuleResponseDto>) => {
      if (!gridInitializedRef.current) return
      if (programmaticSortRef.current) {
        programmaticSortRef.current = false
        return
      }
      const colState = event.api.getColumnState()
      const sorted = colState.find((c) => c.sort != null)
      if (sorted?.colId && sorted.sort) {
        onSortChange?.(sorted.colId, sorted.sort as 'asc' | 'desc')
        return
      }
      onSortChange?.(DEFAULT_SORT.colId, DEFAULT_SORT.sort)
    },
    [onSortChange]
  )

  const headerComponentParams = useMemo(
    () => ({ selectionMode, onHeaderCheckboxClicked }),
    [selectionMode, onHeaderCheckboxClicked]
  )

  const columnDefs = useMemo<
    ColDef<PlanBenefitOverrideRuleResponseDto>[]
  >(() => {
    const sortFor = (colId: string) => applySortModel(colId, initialSortModel)
    return [
      {
        colId: 'selection',
        headerName: '',
        checkboxSelection: true,
        headerComponent: SelectionHeaderCheckbox,
        headerComponentParams,
        pinned: 'left',
        width: 48,
        minWidth: 48,
        maxWidth: 48,
        resizable: false,
        sortable: false,
        suppressSizeToFit: true,
      },
      {
        colId: 'hiosPlanId',
        headerName: 'HIOS Plan ID',
        field: 'match.hiosPlanId',
        valueGetter: (p) => p.data?.match.hiosPlanId,
        pinned: 'left',
        minWidth: 220,
        width: 220,
        sortable: true,
        ...sortFor('hiosPlanId'),
      },
      {
        colId: 'benefitName',
        headerName: 'Benefit',
        field: 'match.benefitName',
        valueGetter: (p) => {
          const name = p.data?.match.benefitName
          if (!name) return ''
          return benefitsByName[name]?.displayName ?? name
        },
        // AG Grid types tooltip value as `any`.
        tooltipValueGetter: (p) => p.value as string,
        pinned: 'left',
        minWidth: 240,
        width: 240,
        sortable: true,
        ...sortFor('benefitName'),
      },
      {
        colId: 'sort',
        headerName: 'Sort',
        valueGetter: (p) => p.data?.params.sort ?? null,
        valueFormatter: numFormatter,
        cellClassRules: notApplicableClassRules('sort'),
        minWidth: 70,
        width: 70,
        sortable: false,
      },
      {
        colId: 'serviceNotCovered',
        headerName: 'Service Not Covered',
        valueGetter: (p) => p.data?.params.serviceNotCovered ?? null,
        valueFormatter: boolFormatter,
        cellClassRules: notApplicableClassRules('serviceNotCovered'),
        minWidth: 160,
        width: 160,
        sortable: false,
      },
      {
        colId: 'appliesToDeductible',
        headerName: 'Applies to Deductible',
        valueGetter: (p) => p.data?.params.appliesToDeductible ?? null,
        valueFormatter: boolFormatter,
        cellClassRules: notApplicableClassRules('appliesToDeductible'),
        minWidth: 170,
        width: 170,
        sortable: false,
      },
      {
        colId: 'isGrouped',
        headerName: 'Is Grouped',
        valueGetter: (p) => p.data?.params.isGrouped ?? null,
        valueFormatter: boolFormatter,
        cellClassRules: notApplicableClassRules('isGrouped'),
        minWidth: 100,
        width: 100,
        sortable: false,
      },
      {
        colId: 'coinsurance',
        headerName: 'Coinsurance',
        valueGetter: (p) => p.data?.params.coinsurance ?? null,
        valueFormatter: numFormatter,
        cellClassRules: notApplicableClassRules('coinsurance'),
        minWidth: 110,
        width: 110,
        sortable: false,
      },
      {
        colId: 'copayAmount',
        headerName: 'Copay Amount',
        valueGetter: (p) => p.data?.params.copayAmount ?? null,
        valueFormatter: currencyFormatter,
        cellClassRules: notApplicableClassRules('copayAmount'),
        minWidth: 120,
        width: 120,
        sortable: false,
      },
      {
        colId: 'copayDayLimit',
        headerName: 'Copay Day Limit',
        valueGetter: (p) => p.data?.params.copayDayLimit ?? null,
        valueFormatter: numFormatter,
        cellClassRules: notApplicableClassRules('copayDayLimit'),
        minWidth: 130,
        width: 130,
        sortable: false,
      },
      {
        colId: 'coinsuranceCopayOrder',
        headerName: 'Coinsurance Copay Order',
        valueGetter: (p) => p.data?.params.coinsuranceCopayOrder ?? null,
        valueFormatter: numFormatter,
        cellClassRules: notApplicableClassRules('coinsuranceCopayOrder'),
        minWidth: 190,
        width: 190,
        sortable: false,
      },
      {
        colId: 'memberServicePaidCap',
        headerName: 'Member Service Paid Cap',
        valueGetter: (p) => p.data?.params.memberServicePaidCap ?? null,
        valueFormatter: currencyFormatter,
        cellClassRules: notApplicableClassRules('memberServicePaidCap'),
        minWidth: 190,
        width: 190,
        sortable: false,
      },
      {
        colId: 'coverageVisitLimit',
        headerName: 'Coverage Visit Limit',
        valueGetter: (p) => p.data?.params.coverageVisitLimit ?? null,
        valueFormatter: numFormatter,
        cellClassRules: notApplicableClassRules('coverageVisitLimit'),
        minWidth: 160,
        width: 160,
        sortable: false,
      },
      {
        colId: 'firstDollarVisits',
        headerName: 'First Dollar Visits',
        valueGetter: (p) => p.data?.params.firstDollarVisits ?? null,
        valueFormatter: numFormatter,
        cellClassRules: notApplicableClassRules('firstDollarVisits'),
        minWidth: 150,
        width: 150,
        sortable: false,
      },
      {
        colId: 'copayAfterFirstVisits',
        headerName: 'Copay After First Visits',
        valueGetter: (p) => p.data?.params.copayAfterFirstVisits ?? null,
        valueFormatter: currencyFormatter,
        cellClassRules: notApplicableClassRules('copayAfterFirstVisits'),
        minWidth: 180,
        width: 180,
        sortable: false,
      },
      {
        colId: 'clusterId',
        headerName: 'Cluster ID',
        valueGetter: (p) => p.data?.params.clusterId ?? null,
        valueFormatter: strFormatter,
        cellClassRules: notApplicableClassRules('clusterId'),
        minWidth: 110,
        width: 110,
        sortable: false,
      },
      {
        colId: 'benefitDisplayValue',
        headerName: 'Benefit Display Value',
        valueGetter: (p) => p.data?.params.benefitDisplayValue ?? null,
        valueFormatter: strFormatter,
        cellClassRules: notApplicableClassRules('benefitDisplayValue'),
        minWidth: 170,
        width: 170,
        sortable: false,
      },
      {
        colId: 'notes',
        headerName: 'Notes',
        valueGetter: (p) => p.data?.params.notes ?? null,
        valueFormatter: strFormatter,
        cellClassRules: notApplicableClassRules('notes'),
        minWidth: 160,
        width: 160,
        sortable: false,
      },
      {
        colId: 'updatedAt',
        headerName: 'Last Updated',
        field: 'updatedAt',
        // AG Grid types value as `any`.
        valueFormatter: (p) =>
          p.value ? relativeTimeString(p.value as string, 30) : '—',
        minWidth: 150,
        width: 150,
        sortable: true,
        ...sortFor('updatedAt'),
      },
      {
        headerName: 'Updated by',
        // Fall back to creator when never edited.
        valueGetter: (p) =>
          p.data?.updatedByEmail ?? p.data?.createdByEmail ?? '—',
        minWidth: 220,
        width: 220,
        sortable: false,
      },
      {
        colId: 'actions',
        headerName: 'Actions',
        cellRenderer: ActionsCellRenderer,
        cellRendererParams: {
          onRowView,
          onRowApprove,
          onRowReject,
          onRowRedraft,
          onRowDelete,
        },
        pinned: 'right',
        width: 100,
        minWidth: 100,
        sortable: false,
      },
    ]
  }, [
    initialSortModel,
    benefitsByName,
    headerComponentParams,
    onRowView,
    onRowApprove,
    onRowReject,
    onRowRedraft,
    onRowDelete,
  ])

  return (
    <>
      <SelectionBanner
        selectionMode={selectionMode}
        selectedCount={selectedCount}
        totalCount={totalCount}
        isMultiPage={isMultiPage}
        onSelectAll={onSelectAll}
        onClearSelection={onClearSelection}
      />
      <div className="ag-theme-alpine grid">
        <AgGridReact
          ref={ref}
          getRowId={getRowId}
          rowModelType="serverSide"
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          headerHeight={56}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          suppressMultiSort={true}
          pagination={true}
          paginationPageSize={defaultPageSize}
          paginationPageSizeSelector={pageSizeOptions}
          cacheBlockSize={defaultPageSize}
          onGridReady={handleGridReady}
          onFirstDataRendered={handleFirstDataRendered}
          onSelectionChanged={handleSelectionChanged}
          onCellClicked={handleCellClicked}
          onPaginationChanged={handlePaginationChanged}
          onSortChanged={handleSortChanged}
          getContextMenuItems={getContextMenuItems}
          rowStyle={ROW_STYLE}
          overlayLoadingTemplate="Loading..."
          overlayNoRowsTemplate="No overrides yet"
          domLayout="autoHeight"
          suppressColumnVirtualisation
        />
      </div>
    </>
  )
})

export default OverrideRulesGrid
